"use client";

import Image from "next/image";
import { Heart, Building2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { GLASS_CARD } from "@/components/glass-card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils/currency";
import { useUserDonationSummary } from "@/app/[locale]/(auth)/user/_api/use-user-queries";
import FieldsOfWorkBadges from "@/app/[locale]/projects/util/field-of-work-bagdes";
import { ImpactActions } from "./impact-actions";
import type { components } from "@/openapi/generated";

type ProjectDetailedDto = components["schemas"]["ProjectDetailedDto"];

interface ImpactHeroCardProps {
  project: ProjectDetailedDto;
  userEnabled: boolean;
}

export function ImpactHeroCard({ project, userEnabled }: ImpactHeroCardProps) {
  const t = useTranslations("impact.dashboard");
  const { data: donations, isLoading } = useUserDonationSummary({ enabled: userEnabled });

  const total = donations?.totalAmount ?? 0;
  const pending = donations?.pendingAmount ?? 0;
  const validated = donations?.validatedAmount ?? 0;
  const received = donations?.receivedAmount ?? 0;
  const denied = donations?.deniedAmount ?? 0;
  const currency = donations?.currency ?? "EUR";

  return (
    <div className={cn(GLASS_CARD, "p-0 overflow-hidden max-w-3xl mx-auto")}>
      {/* Project identity — vertical centered */}
      <div className="flex flex-col items-center text-center p-6 sm:p-8 gap-4">
        <div className="w-32 h-32 rounded-2xl bg-white dark:bg-white/10 flex items-center justify-center overflow-hidden">
          {project.logoUrl ? (
            <Image
              src={project.logoUrl}
              alt={project.name ?? ""}
              width={128}
              height={128}
              className="object-contain w-full h-full p-4"
            />
          ) : (
            <Building2 className="w-12 h-12 text-muted-foreground" />
          )}
        </div>

        <h1 className="text-xl sm:text-2xl font-bold">{project.name}</h1>

        <FieldsOfWorkBadges fields={project.fieldsOfWork ?? []} maxBadges={3} variant="glass" centered />

        <ImpactActions projectName={project.name ?? ""} projectSlug={project.slug} />
      </div>

      {/* Donation stats */}
      <div className="border-t border-white/15 dark:border-white/10 p-5 sm:p-6">
        <h3 className="text-sm font-semibold flex items-center justify-center gap-1.5 mb-3 text-muted-foreground uppercase tracking-wide">
          <Heart className="h-3.5 w-3.5 text-red-500" />
          {t("yourDonations")}
        </h3>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-primary/10 dark:bg-primary/20 p-4 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("total")}</p>
                <p className="text-2xl font-bold text-primary mt-1">{formatCurrency(total, currency)}</p>
              </div>
              <div className="rounded-xl bg-muted/50 dark:bg-muted/30 p-4 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("pending")}</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(pending, currency)}</p>
              </div>
            </div>
            {(validated > 0 || received > 0 || denied > 0) && (
              <div className="flex flex-wrap justify-center gap-3 mt-3 text-xs">
                {validated > 0 && (
                  <span className="text-green-600">{formatCurrency(validated, currency)} {t("validated")}</span>
                )}
                {received > 0 && (
                  <span className="text-blue-500">{formatCurrency(received, currency)} {t("received")}</span>
                )}
                {denied > 0 && (
                  <span className="text-red-500">{formatCurrency(denied, currency)} {t("denied")}</span>
                )}
              </div>
            )}
            {total === 0 && pending === 0 && (
              <p className="text-sm text-muted-foreground mt-2 text-center">{t("noDonationsHint")}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
